import { useState, useEffect } from "react";
import { sqlRead, payloadOf } from "../utils/sqlClient";
import { CRITERIA_DISCIPLINES } from "../config/takeoffCriteria";
import { TakeoffVariablesTable } from "../components/TakeoffVariableRow";
import UtilitySizingStandardsPanel from "../components/UtilitySizingStandardsPanel";
import { btnPrimary } from "../styles/buttons";

// Basis of Design — the master, admin-managed source of truth for:
//   1. Cleanroom Classification (ISPE/EU/FDA grade -> Min/Target ACH), the
//      table that drives Method 1 (ACH-based) room CFM sizing on the Room
//      Takeoff canvas's Mechanical Systems Configuration tab. Moved here
//      (2026-09-09) from that per-project page — the data itself was
//      already global (db/43_cleanroom_classifications.sql), only its admin
//      UI lived somewhere every estimator could reach; that page now shows
//      it read-only with a link back here.
//   2. Every Discipline Criteria dropdown's option list (db/62_basis_of_
//      design_options.sql) — before this page, a select field's choices
//      (Wall panel system, Terminal filtration, Lighting fixtures, ...)
//      were a hardcoded array in src/config/takeoffCriteria.js, so adding
//      or retiring a choice needed a code deploy. Both a project's own
//      Discipline Criteria tab (TakeoffCriteriaPanel.jsx) and Room
//      Templates' Discipline Criteria Defaults (EstimateCostLibrary.jsx)
//      now read live from here (see takeoffCriteria.js's resolveFieldOptions()).
//
//   3. The Duct Estimator's design velocities (TakeoffVariables rows with
//      Category='hvac_duct_sizing', db/65) on the Mechanical/HVAC tab. The
//      room-boundary/drawing-feel Takeoff Variables that briefly had their
//      own tab here moved to Setup ▸ Estimate Engine ▸ Engine Variables on
//      2026-09-11 — they tune the takeoff engine, not a design standard.
//   4. Process Utilities distribution sizing standards (Process & Utilities
//      tab, db/93): per-utility sizing method + velocity band + default
//      material, and the approved pipe materials with their nominal-size
//      tables (UtilitySizingStandardsPanel.jsx). Only chilled water ships
//      configured; every other utility is Sizing Required until approved.
//
// Field DEFINITIONS themselves (which fields exist, labels, CSI codes) stay
// in takeoffCriteria.js, in code — only the selectable VALUES are managed
// here. Same load-read-only -> Edit -> Save/Cancel workflow as every other
// global reference table in this app (Quote Log Setup, Vendors,
// Construction Standards).

import { useIsAdmin, AdminGate } from "../auth/AdminContext";
const ACH_UNITS = ["AC/HR", "FPM"];

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "6px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12.5 };
const inputSm = { width: "100%", fontSize: 12, padding: "4px 7px", border: "1px solid var(--cs-border)", borderRadius: 5, boxSizing: "border-box", background: "var(--surface)", color: "var(--text)" };

// Module-scoped so its identity is stable across parent re-renders — an
// inline component here would remount on every keystroke elsewhere on the
// page, wiping out draft edits mid-type. Moved verbatim from
// MechanicalSystemsPanel.jsx.
function ClassificationRow({ row, editing, onEdit, onCancel, onSave, onDelete }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => { if (editing) setDraft(row); }, [editing, row]);

  if (!editing) {
    return (
      <tr style={{ opacity: row.IsActive ? 1 : 0.5 }}>
        <td style={{ ...td, textAlign: "left" }}>{row.ISPEGrade || "—"}</td>
        <td style={{ ...td, textAlign: "left" }}>{row.EUGrade || "—"}</td>
        <td style={{ ...td, textAlign: "left" }}>{row.FDAGrade || "—"}</td>
        <td style={td}>{row.MinACH ?? "—"} {row.MinACHUnit}</td>
        <td style={{ ...td, fontWeight: 700 }}>{row.TargetACH ?? "—"} {row.TargetACHUnit}</td>
        <td style={td}>{row.EuAtRestParticles || "—"}</td>
        <td style={td}>{row.FdaInOperationParticles || "—"}</td>
        <td style={{ ...td, textAlign: "right" }}>
          <button onClick={onEdit} style={{ fontSize: 11, marginRight: 6, cursor: "pointer" }}>Edit</button>
          {row.IsActive
            ? <button onClick={() => onDelete(row)} style={{ fontSize: 11, cursor: "pointer", color: "#b91c1c" }}>Retire</button>
            : <button onClick={() => onSave({ ...row, IsActive: 1 })} style={{ fontSize: 11, cursor: "pointer", color: "#166534" }}>Reactivate</button>}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={td}><input style={inputSm} value={draft.ISPEGrade || ""} onChange={e => setDraft({ ...draft, ISPEGrade: e.target.value })} /></td>
      <td style={td}><input style={inputSm} value={draft.EUGrade || ""} onChange={e => setDraft({ ...draft, EUGrade: e.target.value })} /></td>
      <td style={td}><input style={inputSm} value={draft.FDAGrade || ""} onChange={e => setDraft({ ...draft, FDAGrade: e.target.value })} /></td>
      <td style={td}>
        <div style={{ display: "flex", gap: 3 }}>
          <input type="number" style={inputSm} value={draft.MinACH ?? ""} onChange={e => setDraft({ ...draft, MinACH: e.target.value === "" ? null : +e.target.value })} />
          <select style={inputSm} value={draft.MinACHUnit || "AC/HR"} onChange={e => setDraft({ ...draft, MinACHUnit: e.target.value })}>
            {ACH_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </td>
      <td style={td}>
        <div style={{ display: "flex", gap: 3 }}>
          <input type="number" style={inputSm} value={draft.TargetACH ?? ""} onChange={e => setDraft({ ...draft, TargetACH: e.target.value === "" ? null : +e.target.value })} />
          <select style={inputSm} value={draft.TargetACHUnit || "AC/HR"} onChange={e => setDraft({ ...draft, TargetACHUnit: e.target.value })}>
            {ACH_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </td>
      <td style={td}><input style={inputSm} value={draft.EuAtRestParticles || ""} onChange={e => setDraft({ ...draft, EuAtRestParticles: e.target.value })} /></td>
      <td style={td}><input style={inputSm} value={draft.FdaInOperationParticles || ""} onChange={e => setDraft({ ...draft, FdaInOperationParticles: e.target.value })} /></td>
      <td style={{ ...td, textAlign: "right" }}>
        <button onClick={() => onSave(draft)} style={{ fontSize: 11, marginRight: 6, cursor: "pointer", fontWeight: 700 }}>Save</button>
        <button onClick={onCancel} style={{ fontSize: 11, cursor: "pointer" }}>Cancel</button>
      </td>
    </tr>
  );
}

// Discipline+field keys whose options are a physical device carrying real
// engineering attributes (db/62's nullable attribute columns) rather than a
// plain descriptive choice. Each attribute set lists the extra columns the
// admin rows show/edit for that field; every other field shows none, and the
// columns stay NULL on its rows. Consumed by src/utils/roomSystemsEngine.js:
//   airflow — Min/Max rated cfm (air_delivery: informational range only)
//   filter  — Min/Design/Max cfm per unit + filter face area: the HEPA
//             count's airflow constraint, coverage constraint, and min/max
//             validation. Design airflow REPLACES the old hardcoded 500 cfm.
//   fixture — lumens / watts / VA / size / spacing criterion: the lighting
//             fixture count (lumen method + spacing check) and its electrical
//             load / HVAC heat gain.
const RANGE_COL = { key: "airflowRange", label: "Rated airflow (cfm)", width: 150 };
const OPTION_ATTRIBUTE_SETS = {
  "mech|air_delivery": [RANGE_COL],
  "mech|filter_type": [
    RANGE_COL,
    { key: "DesignAirflowCfm", label: "Design cfm / unit", step: 1, hint: "Design airflow per filter the HEPA count divides required CFM by. Blank = the HEPA Sizing default below." },
    { key: "FilterFaceAreaSf", label: "Face area (SF)", step: 0.01, hint: "Filter face area for the ceiling-coverage constraint and face velocity (2x4 = 8 SF). Blank = the HEPA Sizing default." },
  ],
  "elec|lighting": [
    { key: "FixtureLumens", label: "Lumens", step: 1, hint: "Rated lumens per fixture — required for the lumen-method fixture count. Blank = no engineering basis; rooms fall back to the template/project fixture count." },
    { key: "FixtureWatts", label: "Watts", step: 0.1, hint: "Input watts per fixture — HVAC lighting heat gain." },
    { key: "FixtureVa", label: "VA", step: 0.1, hint: "Connected VA per fixture — circuit loading. Blank = watts, then the revision's VA/fixture setting." },
    { key: "FixtureLengthFt", label: "L (ft)", step: 0.01 },
    { key: "FixtureWidthFt", label: "W (ft)", step: 0.01 },
    { key: "SpacingCriterion", label: "Spacing crit.", step: 0.01, hint: "Max spacing-to-mounting-height ratio for the uniformity check (flat panel ≈ 1.2–1.5). Blank = the Lighting Design default." },
  ],
};
const attributeColumnsFor = (fieldKey) => OPTION_ATTRIBUTE_SETS[fieldKey] || [];

const fmtAttr = (v) => v == null || v === "" ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 });

// One row of a Discipline Criteria field's option list.
function OptionRow({ row, editing, onEdit, onCancel, onSave, onDelete, attrColumns = [] }) {
  const [draft, setDraft] = useState(row);
  useEffect(() => { if (editing) setDraft(row); }, [editing, row]);

  const numInput = (key, placeholder, step) => (
    <input type="number" step={step ?? "any"} placeholder={placeholder} style={inputSm}
      value={draft[key] ?? ""} onChange={e => setDraft({ ...draft, [key]: e.target.value === "" ? null : +e.target.value })} />
  );

  if (!editing) {
    return (
      <tr style={{ opacity: row.IsActive ? 1 : 0.5 }}>
        <td style={{ ...td, textAlign: "left" }}>{row.OptionValue}</td>
        <td style={td}>{row.DisplayOrder}</td>
        {attrColumns.map(c => (
          <td key={c.key} style={td}>
            {c.key === "airflowRange"
              ? <>{row.MinAirflowCfm ?? "—"}–{row.MaxAirflowCfm ?? "—"}{(row.MinAirflowCfm != null || row.MaxAirflowCfm != null) ? " cfm" : ""}</>
              : fmtAttr(row[c.key])}
          </td>
        ))}
        <td style={{ ...td, textAlign: "right" }}>
          <button onClick={onEdit} style={{ fontSize: 11, marginRight: 6, cursor: "pointer" }}>Edit</button>
          {row.IsActive
            ? <button onClick={() => onDelete(row)} style={{ fontSize: 11, cursor: "pointer", color: "#b91c1c" }}>Retire</button>
            : <button onClick={() => onSave({ ...row, IsActive: 1 })} style={{ fontSize: 11, cursor: "pointer", color: "#166534" }}>Reactivate</button>}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={td}><input style={inputSm} autoFocus value={draft.OptionValue || ""} onChange={e => setDraft({ ...draft, OptionValue: e.target.value })} /></td>
      <td style={td}><input type="number" style={inputSm} value={draft.DisplayOrder ?? 100} onChange={e => setDraft({ ...draft, DisplayOrder: +e.target.value })} /></td>
      {attrColumns.map(c => (
        <td key={c.key} style={td} title={c.hint || ""}>
          {c.key === "airflowRange" ? (
            <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
              {numInput("MinAirflowCfm", "Min", 1)}
              <span style={{ color: "var(--text3)" }}>–</span>
              {numInput("MaxAirflowCfm", "Max", 1)}
            </div>
          ) : numInput(c.key, "—", c.step)}
        </td>
      ))}
      <td style={{ ...td, textAlign: "right" }}>
        <button disabled={!draft.OptionValue} onClick={() => onSave(draft)} style={{ fontSize: 11, marginRight: 6, cursor: "pointer", fontWeight: 700 }}>Save</button>
        <button onClick={onCancel} style={{ fontSize: 11, cursor: "pointer" }}>Cancel</button>
      </td>
    </tr>
  );
}

export default function BasisOfDesign() {
  const isAdmin = useIsAdmin();

  const [tab, setTab] = useState(CRITERIA_DISCIPLINES[1]?.key || CRITERIA_DISCIPLINES[0].key); // default to Mechanical
  const [classifications, setClassifications] = useState([]);
  const [optionRows, setOptionRows] = useState([]);
  const [takeoffVariables, setTakeoffVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editingClassificationId, setEditingClassificationId] = useState(null);
  const [addingClassification, setAddingClassification] = useState(false);
  const [newClassification, setNewClassification] = useState({ TargetACH: "", TargetACHUnit: "AC/HR", MinACHUnit: "AC/HR" });

  const [editingOptionId, setEditingOptionId] = useState(null);
  const [addingToField, setAddingToField] = useState(null); // "disc|fieldKey" or null
  const [newOption, setNewOption] = useState({ OptionValue: "", DisplayOrder: 100 });

  const [editingVariableKey, setEditingVariableKey] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    return Promise.all([
      sqlRead("EXEC sp_GetCleanroomClassifications"),
      sqlRead("EXEC sp_GetBasisOfDesignOptions"),
      sqlRead("EXEC sp_GetTakeoffVariables"),
    ])
      .then(([classificationRows, bodOptionRows, takeoffVariableRows]) => {
        setClassifications(classificationRows || []);
        setOptionRows(bodOptionRows || []);
        setTakeoffVariables(takeoffVariableRows || []);
      })
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  };
  // NOT useEffect(load, []) — load() returns a Promise; see QuoteLogSetup.jsx's
  // identical comment for the exact blank-page bug this avoids.
  useEffect(() => { load(); }, []);

  async function saveClassification(row) {
    try {
      const payload = payloadOf({
        classification_id: row.ClassificationID ?? null,
        ispe_grade: row.ISPEGrade || null,
        eu_grade: row.EUGrade || null,
        fda_grade: row.FDAGrade || null,
        min_ach: row.MinACH ?? null,
        min_ach_unit: row.MinACHUnit || "AC/HR",
        target_ach: row.TargetACH ?? null,
        target_ach_unit: row.TargetACHUnit || "AC/HR",
        eu_at_rest_particles: row.EuAtRestParticles || null,
        fda_in_operation_particles: row.FdaInOperationParticles || null,
        notes: row.Notes || null,
        display_order: row.DisplayOrder ?? 100,
      });
      const result = (await sqlRead(`EXEC sp_SaveCleanroomClassification @payload = '${payload}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      setEditingClassificationId(null);
      setAddingClassification(false);
      setNewClassification({ TargetACH: "", TargetACHUnit: "AC/HR", MinACHUnit: "AC/HR" });
      load();
    } catch (e) { alert("Error saving classification: " + e.message); }
  }

  async function deleteClassification(row) {
    if (!window.confirm(`Retire "${row.FDAGrade || row.ISPEGrade || row.EUGrade}"? Rooms already using it keep their ACH; it just won't offer for new selections.`)) return;
    try {
      const payload = payloadOf({ classification_id: row.ClassificationID, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveCleanroomClassification @payload = '${payload}'`);
      load();
    } catch (e) { alert("Error retiring classification: " + e.message); }
  }

  async function saveOption(discKey, fieldKey, row) {
    try {
      const payload = payloadOf({
        option_id: row.OptionID ?? null,
        discipline: discKey,
        field_key: fieldKey,
        option_value: row.OptionValue,
        display_order: row.DisplayOrder ?? 100,
        min_airflow_cfm: row.MinAirflowCfm ?? null,
        max_airflow_cfm: row.MaxAirflowCfm ?? null,
        // HEPA / lighting engineering attributes (db/62) — null for every
        // field that doesn't show them, so nothing else's rows ever change.
        design_airflow_cfm: row.DesignAirflowCfm ?? null,
        filter_face_area_sf: row.FilterFaceAreaSf ?? null,
        fixture_lumens: row.FixtureLumens ?? null,
        fixture_watts: row.FixtureWatts ?? null,
        fixture_va: row.FixtureVa ?? null,
        fixture_length_ft: row.FixtureLengthFt ?? null,
        fixture_width_ft: row.FixtureWidthFt ?? null,
        spacing_criterion: row.SpacingCriterion ?? null,
      });
      const result = (await sqlRead(`EXEC sp_SaveBasisOfDesignOption @payload = '${payload}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      setEditingOptionId(null);
      setAddingToField(null);
      setNewOption({ OptionValue: "", DisplayOrder: 100 });
      load();
    } catch (e) { alert("Error saving option: " + e.message); }
  }

  async function deleteOption(row) {
    if (!window.confirm(`Retire "${row.OptionValue}"? A project/room template that already picked it keeps that value — it just won't offer for new selections.`)) return;
    try {
      const payload = payloadOf({ option_id: row.OptionID, is_delete: 1 });
      await sqlRead(`EXEC sp_SaveBasisOfDesignOption @payload = '${payload}'`);
      load();
    } catch (e) { alert("Error retiring option: " + e.message); }
  }

  async function saveTakeoffVariable(row) {
    try {
      const payload = payloadOf({ variable_key: row.VariableKey, value: +row.Value });
      const result = (await sqlRead(`EXEC sp_SaveTakeoffVariable @payload = '${payload}'`))?.[0];
      if (result && result.Success === 0) { alert(result.ErrorMessage); return; }
      setEditingVariableKey(null);
      load();
    } catch (e) { alert("Error saving variable: " + e.message); }
  }

  if (isAdmin !== true) return <AdminGate isAdmin={isAdmin} />;
  if (loading) return <div style={{ padding: 32, color: "var(--text3)", fontSize: 13 }}>Loading…</div>;
  if (error) return <div style={{ padding: 32, color: "var(--red)", fontSize: 13 }}>{error}</div>;

  const disc = CRITERIA_DISCIPLINES.find(d => d.key === tab);
  const selectFields = disc ? disc.fields.filter(f => f.type === "select") : [];

  return (
    <div style={{ padding: 32, maxWidth: 980 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Basis of Design</h2>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "var(--text3)" }}>
        Master mechanical/electrical/architectural design standards — the source of truth every estimate's
        Discipline Criteria and Room Templates pull their dropdown choices from, and where the Cleanroom
        Classification (ACH) reference table is now managed. Edited here once; every project and template
        reads live from it.
      </p>

      <div style={{ display: "flex", gap: 16, marginBottom: 16, borderBottom: "1px solid var(--cs-border)", paddingBottom: 8 }}>
        {CRITERIA_DISCIPLINES.map(d => (
          <button key={d.key} onClick={() => setTab(d.key)}
            style={{ fontSize: 12, fontWeight: 600, background: "none", border: "none", cursor: "pointer", color: tab === d.key ? "var(--cs-blue)" : "var(--text3)" }}>
            {d.icon} {d.label}
          </button>
        ))}
      </div>

      {tab === "mech" && (
        <div style={{ marginBottom: 26 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Cleanroom Classification</div>
            {!addingClassification && (
              <button onClick={() => setAddingClassification(true)} style={{ ...btnPrimary, fontSize: 11.5, padding: "4px 10px" }}>+ Add classification</button>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            ISPE/EU/FDA grade correspondence — picking a classification in a room schedule's "Class" column
            populates that room's ACH from Target ACH here (editable per room afterward). Min Accepted is
            reference only; it doesn't drive anything. This is the table Mechanical Systems Configuration
            drives its HVAC sizing from — it's global, not per-project.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>ISPE</th>
                <th style={{ ...th, textAlign: "left" }}>EU</th>
                <th style={{ ...th, textAlign: "left" }}>FDA</th>
                <th style={th}>Min Accepted</th>
                <th style={th}>Target</th>
                <th style={th}>EU At-Rest</th>
                <th style={th}>FDA In-Op</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {classifications.map(row => (
                <ClassificationRow
                  key={row.ClassificationID}
                  row={row}
                  editing={editingClassificationId === row.ClassificationID}
                  onEdit={() => setEditingClassificationId(row.ClassificationID)}
                  onCancel={() => setEditingClassificationId(null)}
                  onSave={saveClassification}
                  onDelete={deleteClassification}
                />
              ))}
              {addingClassification && (
                <ClassificationRow
                  row={newClassification}
                  editing={true}
                  onCancel={() => setAddingClassification(false)}
                  onSave={saveClassification}
                />
              )}
              {classifications.length === 0 && !addingClassification && (
                <tr><td colSpan={8} style={{ ...td, textAlign: "center", color: "var(--text3)" }}>No classifications yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "mech" && (
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Duct Design Velocities</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            Feet-per-minute design velocities the Duct Estimator uses to size trunk/branch/return/exhaust
            duct diameters from CFM. First-pass defaults, editable here instead of only ever changing via
            a code deploy; picked up live the next time the Duct Estimator loads. (The Room Takeoff
            canvas's own room-boundary and drawing constants live under Setup ▸ Estimate Engine ▸ Engine Variables.)
          </div>
          <TakeoffVariablesTable
            rows={takeoffVariables.filter(r => r.Category === "hvac_duct_sizing")}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
            emptyText="No duct design variables yet."
          />
        </div>
      )}

      {tab === "mech" && (
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>HVAC Plant Sizing Standards (CleanSpace standard)</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            Design scenario systems (Takeoff ▸ Mechanical ▸ Design scenarios): design tons = the greater of room sensible / AHU coil load × diversity factor;
            chilled-water flow = design BTUH ÷ (500 × ΔT). These are the company-standard values a system falls back to when its own record names none — diversity
            ships at 1.00 (nothing invented) and ΔT at the conventional 10 °F (44 / 54 °F); edit here when CleanSpace adopts a high-ΔT standard. (db/76)
          </div>
          <TakeoffVariablesTable
            rows={takeoffVariables.filter(r => r.Category === "hvac_plant_sizing")}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
            emptyText="No HVAC plant sizing standards yet — deploy db/76."
          />
        </div>
      )}

      {tab === "mech" && (
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>HEPA Sizing Defaults</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            A room's HEPA count = MAX(required CFM ÷ the selected Terminal filtration option's Design cfm/unit,
            floor area × its HEPA ceiling coverage % ÷ the filter's face area), then any per-room override. The
            option's own attributes (edited in the Terminal filtration list below) always win — these two values
            are only the fallback for an option that hasn't been given them yet. (db/68_hepa_lighting_engine.sql)
          </div>
          <TakeoffVariablesTable
            rows={takeoffVariables.filter(r => r.Category === "hepa_sizing")}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
            emptyText="No HEPA sizing defaults yet — deploy db/68."
          />
        </div>
      )}

      {tab === "mech" && (
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Process Utility Demand Assumptions (CleanSpace standard)</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            Per utility system: design demand = connected demand (active units) × diversity factor; required capacity = design demand × (1 + capacity
            margin). These are the company-standard values a system falls back to when its own record on the Assumed Equipment ▸ Utility demand view
            names no project / scenario / override value — shipped at 1.00 and 0 % so nothing is invented. Add UTILITY_DIVERSITY_&lt;TYPE&gt; /
            UTILITY_CAPACITY_MARGIN_&lt;TYPE&gt; rows for a per-utility standard. Redundancy is a per-system design selection, never a factor. (db/92)
          </div>
          <TakeoffVariablesTable
            rows={takeoffVariables.filter(r => r.Category === "utility_demand")}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
            emptyText="No utility demand assumptions yet — deploy db/92."
          />
        </div>
      )}

      {tab === "process" && (
        <>
          <UtilitySizingStandardsPanel />
          <div style={{ marginTop: 26, marginBottom: 26 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Conceptual Routing Standards (CleanSpace standard)</div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
              Counts the route evaluator derives for the estimate's allowances — never a design: isolation valves per route segment by level (source / plant, main, branch,
              drop), and the default hanger / support spacing used where a pipe size above has none (supports = CEILING(effective LF ÷ spacing) per measured segment).
              Both are priced by material + effective size through "Utility valve" / "Utility support" catalog rows on Estimate Engine ▸ Takeoff mapping. (db/94)
            </div>
            <TakeoffVariablesTable
              rows={takeoffVariables.filter(r => r.Category === "utility_routing")}
              editingKey={editingVariableKey}
              onEdit={setEditingVariableKey}
              onCancel={() => setEditingVariableKey(null)}
              onSave={saveTakeoffVariable}
              emptyText="No routing standards yet — deploy db/94."
            />
          </div>
        </>
      )}

      {tab === "elec" && (
        <div style={{ marginBottom: 26 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Lighting Design Assumptions</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            Lumen-method fixture count per room = CEILING(floor area × target fc ÷ (fixture lumens × CU × LLF)),
            checked against a spacing criterion × mounting height (ceiling − work plane); a room's own
            "Lighting fixtures" criterion is an explicit override. Target fc comes from the Light level option a
            room/template picks; fixture lumens/watts/VA from the Lighting fixtures list below. (db/68)
          </div>
          <TakeoffVariablesTable
            rows={takeoffVariables.filter(r => r.Category === "lighting_design")}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
            emptyText="No lighting design assumptions yet — deploy db/68."
          />
          <div style={{ fontSize: 13, fontWeight: 700, margin: "18px 0 6px" }}>Electrical Demand Factors (CleanSpace standard)</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 6 }}>
            Demand load = connected load × demand factor, per circuit, by load class. These are the company-standard
            factors; a project's Design criteria ▸ Electrical value overrides them for that project, and a circuit's
            own DF in Panels &amp; Circuits overrides both. Seeded at 1.0 (no reduction) — not a code-compliant
            service-sizing rule set; NEC 220 is deliberately not hard-coded. (db/91)
          </div>
          <TakeoffVariablesTable
            rows={takeoffVariables.filter(r => r.Category === "electrical_demand")}
            editingKey={editingVariableKey}
            onEdit={setEditingVariableKey}
            onCancel={() => setEditingVariableKey(null)}
            onSave={saveTakeoffVariable}
            emptyText="No demand factors yet — deploy db/91."
          />
        </div>
      )}

      {disc && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{disc.icon} {disc.label} — Discipline Criteria option lists</div>
          <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 14 }}>
            The choices offered on every "{disc.label}" dropdown, both on a project's own Discipline Criteria tab
            and on Room Templates' Discipline Criteria Defaults.
          </div>
          {selectFields.length === 0 && (
            <div style={{ fontSize: 12.5, color: "var(--text3)" }}>No dropdown fields in this discipline.</div>
          )}
          {selectFields.map(f => {
            const fieldRows = optionRows.filter(r => r.Discipline === disc.key && r.FieldKey === f.key);
            const fieldKey = `${disc.key}|${f.key}`;
            const attrColumns = attributeColumnsFor(fieldKey);
            return (
              <div key={f.key} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700 }}>{f.label}</div>
                  {addingToField !== fieldKey && (
                    <button onClick={() => { setAddingToField(fieldKey); setNewOption({ OptionValue: "", DisplayOrder: (fieldRows.length + 1) * 10 }); }}
                      style={{ ...btnPrimary, fontSize: 11, padding: "3px 10px" }}>+ Add option</button>
                  )}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>Option</th>
                      <th style={{ ...th, width: 90 }}>Order</th>
                      {attrColumns.map(c => <th key={c.key} style={{ ...th, width: c.width ?? 110 }} title={c.hint || ""}>{c.label}</th>)}
                      <th style={{ ...th, textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {fieldRows.map(row => (
                      <OptionRow
                        key={row.OptionID}
                        row={row}
                        editing={editingOptionId === row.OptionID}
                        onEdit={() => setEditingOptionId(row.OptionID)}
                        onCancel={() => setEditingOptionId(null)}
                        onSave={draft => saveOption(disc.key, f.key, draft)}
                        onDelete={deleteOption}
                        attrColumns={attrColumns}
                      />
                    ))}
                    {addingToField === fieldKey && (
                      <OptionRow
                        row={newOption}
                        editing={true}
                        onCancel={() => setAddingToField(null)}
                        onSave={draft => saveOption(disc.key, f.key, draft)}
                        attrColumns={attrColumns}
                      />
                    )}
                    {fieldRows.length === 0 && addingToField !== fieldKey && (
                      <tr><td colSpan={3 + attrColumns.length} style={{ ...td, textAlign: "center", color: "var(--text3)" }}>No options yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
